import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { type ComponentRef, forwardRef } from 'react';

import { ChevronRight } from '@air/next-icons';
import { type VariantProps } from '@air/utils-tailwind';

import { dropdownMenu } from './DropdownMenu';
import { type DropdownMenuItemProps, dropdownMenuItem } from './DropdownMenuItem';

export type DropdownMenuSubTriggerVariant = VariantProps<typeof dropdownMenuItem>;

export type DropdownMenuSubVariant = VariantProps<typeof dropdownMenu>;

export type DropdownMenuSubProps = Pick<
  RadixDropdownMenu.DropdownMenuSubProps,
  'defaultOpen' | 'onOpenChange' | 'open'
> &
  Omit<RadixDropdownMenu.DropdownMenuSubContentProps, 'prefix'> &
  DropdownMenuSubVariant &
  Pick<RadixDropdownMenu.DropdownMenuSubTriggerProps, 'disabled'> &
  Pick<DropdownMenuItemProps, 'description' | 'label' | 'prefix' | 'suffix'> & {
    'data-id'?: string;
    'data-intercomid'?: string;
  };

export const DropdownMenuSub = forwardRef<ComponentRef<typeof RadixDropdownMenu.SubContent>, DropdownMenuSubProps>(
  (
    {
      children,
      className,
      collisionPadding = 8,
      defaultOpen,
      description,
      disabled,
      label,
      onOpenChange,
      open,
      prefix,
      size = 'small',
      suffix,
      'data-id': dataId,
      'data-intercomid': dataIntercomId,
      ...restOfProps
    },
    forwardedRef,
  ) => {
    const hasDescription = !!description;
    const isSmall = size === 'small';

    return (
      <RadixDropdownMenu.Sub defaultOpen={defaultOpen} onOpenChange={onOpenChange} open={open}>
        <RadixDropdownMenu.SubTrigger
          className={dropdownMenuItem({ size })}
          data-id={dataId}
          data-intercomid={dataIntercomId}
          data-testid="DROPDOWN_MENU_SUB_TRIGGER"
          disabled={disabled}
        >
          {prefix ? (
            <div
              className={classNames(
                'mr-2 flex shrink-0 items-center gap-1',
                hasDescription ? (isSmall ? 'mt-0.5' : 'mt-1') : 'mt-0',
              )}
            >
              {prefix}
            </div>
          ) : null}

          <div className="flex min-w-0 grow flex-col">
            <span className={classNames('truncate', isSmall ? 'text-14' : 'text-16')}>{label}</span>
            {hasDescription ? (
              <span className={classNames('text-pigeon-500', isSmall ? 'text-12' : 'text-14')}>{description}</span>
            ) : null}
          </div>

          <div
            className={classNames(
              'ml-2 flex shrink-0 items-center gap-1',
              hasDescription ? (isSmall ? 'mt-0.5' : 'mt-1') : 'mt-0',
            )}
          >
            {suffix}
            <ChevronRight className="size-4" />
          </div>
        </RadixDropdownMenu.SubTrigger>

        <RadixDropdownMenu.Portal>
          <RadixDropdownMenu.SubContent
            className={dropdownMenu({ className, size })}
            collisionPadding={collisionPadding}
            data-testid="DROPDOWN_MENU_SUB"
            ref={forwardedRef}
            {...restOfProps}
          >
            {children}
          </RadixDropdownMenu.SubContent>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Sub>
    );
  },
);

DropdownMenuSub.displayName = 'DropdownMenuSub';
